import _ from 'lodash';
import posthog from 'posthog-js';
import React, { useCallback } from 'react';
import Cookies from 'universal-cookie';

const postHogIdentify = _.memoize(posthog.identify, (...args) => {
  const arrOfString = args.map((arg) => {
    if (typeof arg !== 'string') {
      return JSON.stringify(arg);
    }
    return arg;
  });

  return arrOfString.join('_');
}).bind(posthog);

const getDistinctId = () => {
  const cookies = new Cookies();
  const allCookies = cookies.getAll();
  const envVar = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const key = Object.keys(allCookies).find((key) => key.includes(envVar));
  const jsonData = cookies.get(key);

  return jsonData?.distinct_id;
};

export const postHogReset = (resetDeviceId) => posthog.reset(resetDeviceId);

export const postHogIdentifyUser = (user, geography, educations, workHistories, certifications) => {
  try {
    if (user?.id) {
      const distinctId = getDistinctId();

      postHogIdentify(user.id, {
        email: user.email,
        'First Name': user.firstName,
        'Last Name': user.lastName,
        'Created At': user.createdAt,
        'Approval State': user.approvalState,
        Geography: geography?.longName,
        'Active State': user.activeState,
        'Indeed Resume': user.indeedResume,
        'Missing Required Fields': user.missingRequiredFields,
        'Visibility State': user.visibilityState,
        'Work History Count': workHistories?.length || 0,
        'Education Count': educations?.length || 0,
        'Certifications Count': certifications?.length || 0,
      });

      if (Boolean(distinctId) && user.id !== distinctId) {
        posthog.alias(user.id, distinctId);
      }
    }
  } catch (e) {
    console.log('POSTHOG ERROR', e);
  }
};

export const postHogIdentifyEmployer = (
  employer,
  subscription,
  geography,
  currentEmployerProfile,
) => {
  try {
    if (employer?.id) {
      const distinctId = getDistinctId();
      const planName = subscription?.plan()?.name;
      const status = subscription?.attributes()?.status;

      postHogIdentify(employer.id, {
        email: employer.email,
        'Employer Name': employer.name,
        'Created At': employer.createdAt,
        Geography: geography?.longName,
        'First Name': currentEmployerProfile?.firstName,
        'Last Name': currentEmployerProfile?.lastName,
        'Plan Name': planName,
        'Subscription Status': status,
      });

      if (Boolean(distinctId) && employer.id !== distinctId) {
        posthog.alias(employer.id, distinctId);
      }
    }
  } catch (e) {
    console.log('POSTHOG ERROR', e);
  }
};

export const postHogCapture = (debounced = true) =>
  debounced
    ? _.debounce((eventName, properties = {}) => {
        posthog.capture(eventName, { ...properties });
      }, 500)
    : (eventName, properties = {}) => {
        posthog.capture(eventName, { ...properties });
      };

function PostHog({
  children,
  eventName = '',
  properties = {},
  override = 'onClick',
  clone = true,
  className = '',
  noDebounce = false,
}) {
  const eventCapture = useCallback(postHogCapture(!noDebounce), [properties]);

  if (!clone) {
    return (
      <span
        className={className}
        onClick={() => {
          eventCapture(eventName, properties);
        }}
      >
        {children}
      </span>
    );
  }

  return React.cloneElement(children, {
    ...children.props,
    [override]: (...args) => {
      children.props[override]?.(...args);
      eventCapture(eventName, properties);
    },
  });
}

export default PostHog;
